import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/custom/custom-toast";
import { PAGE_SIZES, STATUS } from "../../../constants/status";
import { STRING_SUB_MENU } from "../../../constants/string";
import { useFetchData } from "../../../hooks/useFetch";
import { CompaniesResponse } from "../../../models/response/companies";
import { ConfigsPublicModel } from "../../../models/response/configs-public";
import { CountTabResponse } from "../../../models/response/count-tab-response";
import { API_COMPANIES_ROUTER, API_CONFIGS_ROUTER, ApiConfig } from "../../../services/api-config";
import { BasePaginateResponse, isSuccess } from "../../../services/base-response";
import { toggleMenuContent } from "../../../store/themeConfigSlice";
import CookieUtils from "../../../utils/cookie-utils";
import { CompaniesHeader } from "../components/companies-header";
import { CompaniesTable } from "../components/companies-table";
import { CompaniesChangeStatus } from "../components/modal/companies-change-status";
import { CompaniesCreate } from "../components/modal/companies-create";
import { CompaniesDetail } from "../components/modal/companies-detail";
import { CompaniesUpdate } from "../components/modal/companies-update";

export const CompaniesPage = () => {
  const dispatch = useDispatch();
  dispatch(toggleMenuContent(STRING_SUB_MENU.COMPANIES));
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isOpenChangeStatus, setIsOpenChangeStatus] = useState(false);
  const [dataObject, setDataObject] = useState<CompaniesResponse>(new CompaniesResponse());
  const [countTab, setCountTab] = useState<CountTabResponse>(new CountTabResponse());
  const [totalRecords, setTotalRecords] = useState(0);
  const [dataCompanies, setDataCompanies] = useState<CompaniesResponse[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [keySearch, setKeySearch] = useState("");
  const [status, setStatus] = useState(STATUS.ACTIVE);

  const { request: fetchDataCountTab, baseResponse: responses } = useFetchData.Get<CountTabResponse>();

  const {
    request: fetchDataCompanies,
    baseResponse: resCompanies,
    loading,
  } = useFetchData.Get<BasePaginateResponse<CompaniesResponse[]>>();

  const { request: fetchGetConfigsPublic, baseResponse: resConfigsPublic } = useFetchData.Get<ConfigsPublicModel>({
    apiPublic: true,
    projectId: ApiConfig.PROJECT_ID.UPLOAD_SERVICE,
  });

  useEffect(() => {
    fetchGetConfigsPublic({
      endPoint: API_CONFIGS_ROUTER.API_CONFIGS_PUBLIC(),
    });
  }, []);

  useEffect(() => {
    if (!resConfigsPublic) return;
    if (isSuccess(resConfigsPublic)) {
      CookieUtils.setCookie("domain", JSON.stringify(resConfigsPublic.data));
    } else {
      showToast({ message: resConfigsPublic.message, type: "warning" });
    }
  }, [resConfigsPublic]);

  const handleCountTab = useCallback(() => {
    fetchDataCountTab({
      endPoint: API_COMPANIES_ROUTER.API_GET_COMPANIES_COUNT_TAB,
      data: {
        key_search: keySearch,
      },
    });
    setRefresh(false);
  }, [fetchDataCountTab, keySearch]);

  useEffect(() => {
    handleCountTab();
  }, []);

  useEffect(() => {
    if (refresh) handleCountTab();
  }, [refresh]);

  useEffect(() => {
    if (!responses) return;
    if (responses.status === 200) {
      setCountTab(responses.data);
    }
  }, [responses]);

  const handleData = () => {
    fetchDataCompanies({
      endPoint: API_COMPANIES_ROUTER.API_GET_COMPANIES,
      data: {
        status: status,
        key_search: keySearch,
        page: page,
        limit: limit,
      },
    });
    setRefresh(false);
  };

  useEffect(() => {
    if (!resCompanies) return;
    if (resCompanies.status === 200) {
      setDataCompanies(resCompanies.data.list);
      setTotalRecords(resCompanies.data.total_record);
    } else {
      showToast({ message: resCompanies.message, type: "error" });
    }
  }, [resCompanies]);

  useEffect(() => {
    setPage(1);
  }, [keySearch, status]);

  useEffect(() => {
    if (refresh) handleData();
  }, [refresh]);
  useEffect(() => {
    handleData();
  }, [status, keySearch, limit, page]);

  return (
    <div className="flex flex-col gap-3">
      <CompaniesHeader setShowModalCreate={setIsOpenCreate} countTab={countTab} setStatus={setStatus} />
      <CompaniesTable
        loadingTable={loading}
        dataCompanies={dataCompanies}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        keySearch={keySearch}
        setKeySearch={setKeySearch}
        totalRecords={totalRecords}
        setOpenModalUpdate={setIsOpenUpdate}
        setOpenModalDetail={setIsOpenDetail}
        setOpenModalChangeStatus={setIsOpenChangeStatus}
        setDataObject={setDataObject}
      />
      <CompaniesCreate isOpen={isOpenCreate} setIsOpen={setIsOpenCreate} setRefresh={setRefresh} />
      {isOpenUpdate && (
        <CompaniesUpdate
          isOpen={isOpenUpdate}
          setIsOpen={setIsOpenUpdate}
          dataObject={dataObject}
          setRefresh={setRefresh}
        />
      )}
      <CompaniesDetail isOpen={isOpenDetail} setIsOpen={setIsOpenDetail} dataObject={dataObject} />
      <CompaniesChangeStatus
        isOpen={isOpenChangeStatus}
        setIsOpen={setIsOpenChangeStatus}
        dataObject={dataObject}
        setRefresh={setRefresh}
      />
    </div>
  );
};
